import type { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import React from 'react'
import { GeckoMeasurement } from 'types'

import { SectionStatsTable } from './SectionStatsTable'
import { SectionStatsText } from './SectionStatsText'

export interface SectionStats {
  sectionName: string
  minThickness: GeckoMeasurement
  averageThickness: GeckoMeasurement
  wallLossPercent?: GeckoMeasurement
  nominalThickness?: GeckoMeasurement
  tMin?: GeckoMeasurement
}

export interface ComponentStats {
  componentId: string
  componentName: string
  isWholeComponent: boolean
  sections: SectionStats[]
}

interface InspectionSummaryStatsProps {
  componentStats: ComponentStats[]
  showComponentNames: boolean
  useTable: boolean
  includeTMin: boolean
  formatMeasurement: ReturnType<typeof useRugReportOps>['formatMeasurement']
}

export const InspectionSummaryStats: React.FC<InspectionSummaryStatsProps> = ({
  componentStats,
  showComponentNames,
  useTable,
  includeTMin,
  formatMeasurement,
}) => {
  const sectionStats = componentStats.flatMap(({ sections }) => sections)
  // Keep paginated groups as siblings; nested containers can strand an empty first fragment.
  return (
    <>
      {showComponentNames ? (
        componentStats.map(
          ({ componentId, componentName, isWholeComponent, sections }) => (
            <React.Fragment key={componentId}>
              {useTable ? (
                <SectionStatsTable
                  formatMeasurement={formatMeasurement}
                  componentName={componentName}
                  sectionStats={sections}
                  includeTMin={includeTMin}
                />
              ) : (
                sections.map((section, index) => (
                  <SectionStatsText
                    key={section.sectionName}
                    componentName={index === 0 ? componentName : undefined}
                    formatMeasurement={formatMeasurement}
                    sectionName={section.sectionName}
                    minThickness={section.minThickness}
                    averageThickness={section.averageThickness}
                    wallLossPercent={section.wallLossPercent}
                    nominalThickness={section.nominalThickness}
                    showSectionName={!isWholeComponent}
                  />
                ))
              )}
            </React.Fragment>
          )
        )
      ) : useTable ? (
        <SectionStatsTable
          formatMeasurement={formatMeasurement}
          sectionStats={sectionStats}
          includeTMin={includeTMin}
        />
      ) : (
        sectionStats.map((section) => (
          <SectionStatsText
            key={section.sectionName}
            formatMeasurement={formatMeasurement}
            sectionName={section.sectionName}
            minThickness={section.minThickness}
            averageThickness={section.averageThickness}
            wallLossPercent={section.wallLossPercent}
            nominalThickness={section.nominalThickness}
          />
        ))
      )}
    </>
  )
}
